/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { updateOrderBasedOnIndex } from '@/utility/utils';
import {
  BookModel,
  DisplayTypes,
  PageCanvasModel,
  PageItemCanvasModel,
  PageModel,
} from '@/types';
import { set } from '@/data/ionicStorage';

interface SmartBookState {
  book: BookModel | null;
  contentInfo: string;
  currentPageNumber: number;
  currentPage: PageCanvasModel | null;
  currentPageItem: PageItemCanvasModel |null;
  croppedArea: any;
  displayType: DisplayTypes;
  previousDisplayType: DisplayTypes;
  bookName: string;
  bookOptions: {
    pagingOptions: {
      sortClause: string;
      orderBy: string;
      pageIndex: number;
      pageSize: number;
    };
    filterSpec: {
      title: string;
      isbn: string;
      classIds: number[];
      lessonIds: number[];
    };
  };
}

// ** Initial State
const initialState: SmartBookState = {
  book: null,
  contentInfo: '',
  currentPageNumber: 1,
  currentPage: null,
  currentPageItem: null,
  croppedArea: null,
  displayType: DisplayTypes.Page,
  previousDisplayType: DisplayTypes.Page,
  bookName: '',
  bookOptions: {
    pagingOptions: {
      sortClause: 'decs',
      orderBy: 'id',
      pageIndex: 0,
      pageSize: 5,
    },
    filterSpec: {
      title: '',
      isbn: '',
      classIds: [],
      lessonIds: [],
    },
  },
};

const getBookPageByNumber = async (
  book: BookModel,
  pageNo: number,
): Promise<PageModel | null> => {
  const currentPage = book.pages.find((x) => x.pageNo === pageNo);
  if (currentPage) {
    await set(`${book.id}-lastPage`, pageNo.toString());
    const pageItems = updateOrderBasedOnIndex(currentPage.pageItems);
    let tempPage: PageModel = { ...currentPage };
    if (pageItems) {
      tempPage = { ...tempPage, pageItems };
    }
    return tempPage;
  }
  return null;
};


export const getBookPage = createAsyncThunk(
  'smartBook/getBookPage',
  async ({
    book,
    pageNo,
  }: {
    book: BookModel;
    pageNo: number;
    userId: number;
  }) => {
    const page = await getBookPageByNumber(book, pageNo);
    const currentPage = page;
    return { currentPage };
  },
);

// ** Slice
export const smartBookSlice = createSlice({
  name: 'smartBook',
  initialState,
  reducers: {
    setBook: (state, action: PayloadAction<BookModel>) => {
      state.book = action.payload;
    },
    setDisplayType: (state, action) => {
      state.previousDisplayType = state.displayType;
      state.displayType = action.payload;
    },
    setContentInfo: (state, action: PayloadAction<number>) => {
      let title = '';
      const unit = state.book?.units?.find(
        (x) =>
          x.startPage !== undefined &&
          x.endPage !== undefined &&
          x.startPage <= action.payload &&
          x.endPage >= action.payload,
      );
      if (unit) {
        title = unit.name;
        const test = unit.unitContentModels.find(
          (x) =>
            x.startPage !== undefined &&
            x.endPage !== undefined &&
            x.startPage <= action.payload &&
            x.endPage >= action.payload,
        );
        if (test) {
          title += `/${test.name}`;
        }
      }
      state.contentInfo = title;
    },
    setCurrentPage: (state, action: PayloadAction<PageCanvasModel>) => {
      state.currentPage = action.payload;
      let title = '';
      const unit = state.book?.units?.find(
        (x) =>
          x.startPage !== undefined &&
          x.endPage !== undefined &&
          x.startPage <= action.payload.pageNo &&
          x.endPage >= action.payload.pageNo,
      );
      if (unit) {
        title = unit.name;
        const test = unit.unitContentModels.find(
          (x) =>
            x.startPage !== undefined &&
            x.endPage !== undefined &&
            x.startPage <= action.payload.pageNo &&
            x.endPage >= action.payload.pageNo,
        );
        if (test) {
          title += `/${test.name}`;
        }
      }
      state.contentInfo = title;
    },
    setCurrentPageItem: (state, action) => {
      state.currentPageItem = action.payload;
      if(state.displayType !== DisplayTypes.PageItem){
        state.displayType = DisplayTypes.PageItem;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getBookPage.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.currentPage = action.payload.currentPage;
      },
    );
  },
});

// ** Export Actions and Reducer
export const {
  setBook,
  setDisplayType,
  setCurrentPage,
  setCurrentPageItem,
  setContentInfo,
} = smartBookSlice.actions;

export default smartBookSlice.reducer;
